import { Message, Widget, Product, ProductResultGroup } from "@/types";
import i18n from "@/lib/i18n";
import {
  BackendChatMessage,
  BackendMessageContent,
  BackendConversationMessagesResponse,
  BackendInfiniteScrollResponse,
  WebSearchProduct,
} from "../models/api-types";
import { normalizeFilterOptions } from "@/features/instantsearch/services/filter-normalizer";
import {
  parseExternalChatLinks,
  parseWebSearchProducts,
} from "../utils/chat-artifacts";
import { parseProductComparison } from "@/features/product-comparison/model";

export class MessageTransformer {
  /**
   * Backend chat item listesini frontend Message array'ine çevirir.
   * Her chat item 2 mesaja açılır: prompt (user) + response (assistant).
   */
  private static transformChatItems(chatItems: BackendChatMessage[]): Message[] {
    const messages: Message[] = [];

    chatItems.forEach((chatItem) => {
      const userMessage = this.createUserMessage(chatItem);
      if (userMessage) {
        messages.push(userMessage);
      }

      const assistantMessage = this.createAssistantMessage(chatItem);
      if (assistantMessage) {
        messages.push(assistantMessage);
      }
    });

    return messages;
  }

  /**
   * Backend conversation response'unu frontend Message array'ine çevirir
   */
  static transformConversationToMessages(
    response: BackendConversationMessagesResponse
  ): Message[] {
    return this.transformChatItems(response.data.chat);
  }

  /**
   * Infinite scroll response'unu frontend Message array'ine çevirir
   */
  static transformInfiniteScrollToMessages(
    response: BackendInfiniteScrollResponse
  ): Message[] {
    return this.transformChatItems(response.data.messages);
  }

  /**
   * Chat item'ından user mesajı oluşturur
   */
  private static createUserMessage(
    chatItem: BackendChatMessage
  ): Message | null {
    if (!chatItem.prompt || chatItem.prompt.length === 0) {
      return null;
    }

    // Prompt array'indeki text content'leri birleştir
    const textContents = chatItem.prompt
      .filter((p) => p.type === "text" && p.content)
      .map((p) => p.content)
      .join(" ");

    // Attachments'lardan image URL'lerini topla
    const images: string[] = [];
    chatItem.prompt.forEach((p) => {
      if (p.attachments && p.attachments.length > 0) {
        p.attachments.forEach((attachment) => {
          // Sadece image type'ları ekle
          if (attachment.fileType.startsWith("image/")) {
            images.push(attachment.url);
          }
        });
      }
    });

    return {
      id: `${chatItem._id}_user`,
      content:
        textContents ||
        (images.length > 0 ? i18n.t("chat.imageMessageFallback") : ""),
      role: "user",
      timestamp: new Date(chatItem.createdAt),
      images: images.length > 0 ? images : undefined,
    };
  }

  /**
   * Chat item'ından assistant mesajı oluşturur
   */
  private static createAssistantMessage(
    chatItem: BackendChatMessage
  ): Message | null {
    if (!chatItem.response || chatItem.response.length === 0) {
      return null;
    }

    // Text content'leri birleştir
    const textContents = chatItem.response
      .filter(
        (r) =>
          // Normal text mesajları
          (r.type === "text" || r.type === "vectorSearch") &&
          r.content &&
          !r.isCard
      )
      .map((r) => r.content)
      .join("\n\n");

    // Attachments'lardan image URL'lerini topla
    const images: string[] = [];
    chatItem.response.forEach((r) => {
      if (!r.isCard && r.attachments && r.attachments.length > 0) {
        r.attachments.forEach((attachment) => {
          // Sadece image type'ları ekle
          if (attachment.fileType.startsWith("image/")) {
            images.push(attachment.url);
          }
        });
      }
    });

    // Card content'leri widget'lara çevir (mesaj ID'si stabil widget ID'leri için)
    const widgets = this.transformCardsToWidgets(chatItem.response, chatItem._id);

    // Products varsa (card content'lerden)
    const products = this.transformCardsToProducts(chatItem.response);

    // 🆕 VectorSearch product ID'lerini extract et
    const vectorSearchProductIds = this.extractVectorSearchProductIds(
      chatItem.response
    );
    const vectorSearchOfferMap = this.extractVectorSearchOfferMap(
      chatItem.response,
      vectorSearchProductIds
    );

    // 🆕 VectorSearch filterOptions'ı extract et
    const vectorSearchFilterOptions = this.extractVectorSearchFilterOptions(
      chatItem.response
    );

    const vectorSearchResultGroups = this.extractVectorSearchResultGroups(
      chatItem.response
    );

    // 🆕 WebSearch ürün kartlarını extract et
    const { products: webProducts, query: webSearchQuery } =
      this.extractWebSearchProducts(chatItem.response);

    // 🆕 Turun sunduğu uygulama dışı bağlantıları extract et
    const externalLinks = this.extractExternalLinks(chatItem.response);
    const productComparison = this.extractProductComparison(chatItem.response);

    // 🎨 Library items'ları topla (R2 creative generation sonuçları)
    const libraryItems = this.transformLibraryItems(chatItem.response);

    // 🎨 Original prompt'u topla (edit için)
    const originalPrompt = chatItem.prompt
      ?.filter((p) => p.type === "text" && p.content)
      .map((p) => p.content)
      .join(" ");

    // 🤖 Model suggestion kontrolü ve extraction
    const modelSuggestionResponse = chatItem.response.find(
      (r) =>
        (r as any).type === "modelSuggestion" ||
        (r as any).modelSuggestion !== undefined
    );

    const modelSuggestions = modelSuggestionResponse
      ? ((modelSuggestionResponse as any).modelSuggestion as Array<any>)
      : undefined;

    return {
      id: `${chatItem._id}_assistant`,
      content: textContents || "",
      role: "assistant",
      timestamp: new Date(chatItem.createdAt),
      images: images.length > 0 ? images : undefined,
      widgets: widgets.length > 0 ? widgets : undefined,
      products: products.length > 0 ? products : undefined,
      productIds:
        vectorSearchProductIds.length > 0 ? vectorSearchProductIds : undefined, // 🆕 VectorSearch product ID'leri
      offerMap:
        Object.keys(vectorSearchOfferMap).length > 0
          ? vectorSearchOfferMap
          : undefined,
      filterOptions:
        Object.keys(vectorSearchFilterOptions).length > 0
          ? vectorSearchFilterOptions
          : undefined, // 🆕 VectorSearch filterOptions
      resultGroups:
        vectorSearchResultGroups.length > 0
          ? vectorSearchResultGroups
          : undefined,
      webProducts: webProducts.length > 0 ? webProducts : undefined,
      webSearchQuery: webSearchQuery || undefined,
      externalLinks: externalLinks.length > 0 ? externalLinks : undefined,
      productComparison,
      libraryItems: libraryItems.length > 0 ? libraryItems : undefined,
      originalPrompt: originalPrompt || undefined,
      selectedModel:
        chatItem.modelInfo?.modelName || chatItem.model || undefined,
      feedback: typeof chatItem.feedback === "number" ? chatItem.feedback : null,
      hasModelSuggestion: modelSuggestions ? true : undefined,
      modelSuggestions: modelSuggestions || undefined,
    };
  }

  /**
   * Backend card content'leri frontend Widget'lara çevirir
   */
  private static transformCardsToWidgets(
    responses: BackendMessageContent[],
    messageId: string
  ): Widget[] {
    const widgets: Widget[] = [];

    responses
      .filter((r) => r.type === "card" && r.isCard && r.cardContent)
      .forEach((r, responseIndex) => {
        r.cardContent.forEach((card, index) => {
          // Card'ı widget formatına çevir
          // Stabil ID: Date.now() her refetch'te farklı ID üretip
          // React key kararsızlığına yol açıyordu.
          widgets.push({
            id: `${messageId}_widget_${responseIndex}_${index}`,
            key: this.generateWidgetKey(card.title),
            name: card.title,
            icon: card.file,
            credits: card.price,
            model_name: card.type,
          });
        });
      });

    return widgets;
  }

  /**
   * Backend card content'leri frontend Product'lara çevirir
   */
  private static transformCardsToProducts(
    responses: BackendMessageContent[]
  ): Product[] {
    const products: Product[] = [];

    responses
      .filter((r) => r.type === "card" && r.isCard && r.cardContent)
      .forEach((r) => {
        r.cardContent.forEach((card) => {
          // Eğer image tipindeyse ve ürün benzeri görünüyorsa Product'a çevir
          if (card.type === "image" && card.file && card.title) {
            products.push({
              brand_logo: "", // Backend'de logo bilgisi yok, empty bırak
              image: card.file,
              link: "#", // Backend'de link yok, placeholder
              price: `$${card.price}`,
              title: card.title,
            });
          }
        });
      });

    return products;
  }

  /**
   * 🆕 VectorSearch response'undan product ID'lerini extract eder
   */
  private static extractVectorSearchProductIds(
    responses: BackendMessageContent[]
  ): string[] {
    const productIds: string[] = [];

    responses
      .filter((r) => {
        // Type assertion: Backend'den vectorSearch type'ı gelebilir
        const response = r as any;
        return (
          response.type === "vectorSearch" &&
          response.vectorSearch &&
          response.vectorSearch.products
        );
      })
      .forEach((r) => {
        const vectorSearch = (r as any).vectorSearch;
        if (Array.isArray(vectorSearch.products)) {
          productIds.push(
            ...vectorSearch.products
              .filter((productId: unknown): productId is string =>
                typeof productId === "string"
              )
              .map((productId: string) => productId.trim())
              .filter(Boolean)
          );
        }
      });

    return productIds;
  }

  private static extractVectorSearchOfferMap(
    responses: BackendMessageContent[],
    productIds: string[]
  ): Record<string, string> {
    const allowedProductIds = new Set(productIds);
    const offerMap: Record<string, string> = {};
    let hasConflict = false;

    responses.forEach((response) => {
      const rawOfferMap = response.vectorSearch?.offerMap;
      if (
        typeof rawOfferMap !== "object" ||
        rawOfferMap === null ||
        Array.isArray(rawOfferMap)
      ) {
        return;
      }

      Object.entries(rawOfferMap).forEach(([rawProductId, offerId]) => {
        const productId = rawProductId.trim();
        const normalizedOfferId =
          typeof offerId === "string" ? offerId.trim() : "";
        if (
          allowedProductIds.has(productId) &&
          normalizedOfferId.length > 0
        ) {
          if (
            offerMap[productId] !== undefined &&
            offerMap[productId] !== normalizedOfferId
          ) {
            hasConflict = true;
            return;
          }
          offerMap[productId] = normalizedOfferId;
        }
      });
    });

    return hasConflict ? {} : offerMap;
  }

  /**
   * VectorSearch response'undan filterOptions'ı extract eder.
   * Backend { categories: [{id,name}], brand: [{id,name}] } formatını
   * store'un ID/name string[] formatına çevirir.
   */
  private static extractVectorSearchFilterOptions(
    responses: BackendMessageContent[]
  ): Record<string, string[]> {
    const mergedRaw: Record<string, unknown> = {};

    responses
      .filter((r) => {
        const response = r as BackendMessageContent;
        return (
          response.type === "vectorSearch" &&
          response.vectorSearch &&
          response.vectorSearch.filterOptions
        );
      })
      .forEach((r) => {
        const vectorSearch = (r as BackendMessageContent).vectorSearch;
        if (
          vectorSearch?.filterOptions &&
          typeof vectorSearch.filterOptions === "object"
        ) {
          Object.entries(vectorSearch.filterOptions).forEach(([key, value]) => {
            if (!Array.isArray(value) || value.length === 0) return;

            const existing = mergedRaw[key];
            if (Array.isArray(existing)) {
              mergedRaw[key] = [...existing, ...value];
            } else {
              mergedRaw[key] = [...value];
            }
          });
        }
      });

    return normalizeFilterOptions(mergedRaw);
  }

  /**
   * Tek mesajdaki bağımsız katalog aramalarını ayrı carousel gruplarına çevirir.
   * Düz productIds/filterOptions alanları eski mesajlar için korunur.
   */
  private static extractVectorSearchResultGroups(
    responses: BackendMessageContent[]
  ): ProductResultGroup[] {
    const groups: ProductResultGroup[] = [];

    responses.forEach((response) => {
      const rawGroups = response.vectorSearch?.resultGroups;
      if (!Array.isArray(rawGroups)) return;

      rawGroups.forEach((group) => {
        const label = typeof group.label === "string" ? group.label.trim() : "";
        const recipient = group.recipient;
        const productIds = Array.isArray(group.productIds)
          ? group.productIds
              .filter(
                (productId): productId is string =>
                  typeof productId === "string"
              )
              .map((productId) => productId.trim())
              .filter(Boolean)
          : [];
        const webProducts = parseWebSearchProducts(group.webProducts);
        // Grup ya katalog kartıyla ya web kartıyla var olur: katalogun
        // cevaplayamadığı istek de kullanıcının istediği şeylerden biri.
        if (
          !label ||
          !["self", "other", "unknown"].includes(recipient) ||
          (productIds.length === 0 && webProducts.length === 0)
        ) {
          return;
        }

        const filterOptions = normalizeFilterOptions(
          group.filterOptions ?? {}
        );
        const offerMap: Record<string, string> = {};
        let hasOfferConflict = false;
        Object.entries(group.offerMap ?? {}).forEach(
          ([rawProductId, offerId]) => {
            const productId = rawProductId.trim();
            const normalizedOfferId =
              typeof offerId === "string" ? offerId.trim() : "";
            if (!productIds.includes(productId) || !normalizedOfferId) return;
            if (
              offerMap[productId] !== undefined &&
              offerMap[productId] !== normalizedOfferId
            ) {
              hasOfferConflict = true;
              return;
            }
            offerMap[productId] = normalizedOfferId;
          }
        );
        const safeOfferMap = hasOfferConflict ? {} : offerMap;
        const discoverQuery = typeof group.discoverQuery === "string"
          ? group.discoverQuery.trim() : "";
        groups.push({
          label,
          ...(discoverQuery && discoverQuery !== "*" ? { discoverQuery } : {}),
          recipient,
          productIds,
          offerMap:
            Object.keys(safeOfferMap).length > 0 ? safeOfferMap : undefined,
          filterOptions:
            Object.keys(filterOptions).length > 0 ? filterOptions : undefined,
          ...(webProducts.length > 0
            ? {
                webProducts,
                webQuery:
                  typeof group.webQuery === "string" ? group.webQuery : "",
              }
            : {}),
        });
      });
    });

    return groups;
  }

  /**
   * WebSearch response'undan ürün kartlarını extract eder
   */
  private static extractWebSearchProducts(
    responses: BackendMessageContent[]
  ): { products: WebSearchProduct[]; query: string | null } {
    const products: WebSearchProduct[] = [];
    let query: string | null = null;

    responses
      .filter(
        (r) =>
          r.type === "webSearch" &&
          r.webSearch &&
          Array.isArray(r.webSearch.products)
      )
      .forEach((r) => {
        if (!r.webSearch) return;
        if (!query && r.webSearch.query) {
          query = r.webSearch.query;
        }
        products.push(...parseWebSearchProducts(r.webSearch.products));
      });

    // position'a göre sırala (varsa)
    products.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return { products, query };
  }

  /**
   * `externalLink` response'larından bağlantıları extract eder.
   *
   * Backend bu bağlantıları kalıcı olarak kaydediyor çünkü cevabın metni
   * onlardan söz ediyor ("kargonu buradan takip edebilirsin"); yalnızca canlı
   * SSE event'inde kalsalardı sayfa yenilendiğinde metin bir hiçe atıfta
   * bulunurdu. Aynı bağlantı birden fazla entry'de görünebildiği için URL'e
   * göre tekilleştiriliyor.
   */
  private static extractExternalLinks(
    responses: BackendMessageContent[]
  ): Array<{ url: string; label: string }> {
    return parseExternalChatLinks(
      responses
        .filter(
          (response) =>
            response.type === "externalLink" &&
            Array.isArray(response.externalLinks),
        )
        .flatMap((response) => response.externalLinks ?? []),
    );
  }

  /** Son `productComparison` kaydı. İki üründen azı buton üretmez. */
  private static extractProductComparison(responses: BackendMessageContent[]) {
    let snapshot: ReturnType<typeof parseProductComparison> = null;
    for (const response of responses) {
      if (response.type !== "productComparison") continue;
      const parsed = parseProductComparison(response.productComparison);
      if (parsed) snapshot = parsed;
    }
    return snapshot ?? undefined;
  }

  /**
   * Card title'dan widget key oluşturur
   */
  private static generateWidgetKey(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }

  /**
   * Backend library content'lerini frontend message library items'a çevirir
   */
  private static transformLibraryItems(
    responses: BackendMessageContent[]
  ): Array<{
    _id: string;
    title: string;
    type: string;
    file: {
      type: string;
      size: number;
      folder: string;
      fileUrl: string;
    };
  }> {
    const libraryItems: Array<{
      _id: string;
      title: string;
      type: string;
      file: {
        type: string;
        size: number;
        folder: string;
        fileUrl: string;
      };
    }> = [];

    responses
      .filter((r) => r.type === "library" && r.library)
      .forEach((r) => {
        if (r.library) {
          libraryItems.push({
            _id: r.library._id,
            title: r.library.title,
            type: r.library.type,
            file: {
              type: r.library.file.type,
              size: r.library.file.size,
              folder: r.library.file.folder,
              fileUrl: r.library.file.fileUrl,
            },
          });
        }
      });

    return libraryItems;
  }
}
